import React, { useEffect, useState } from 'react'

import { createTheme, darken, lighten } from '@material-ui/core/styles'
import { ptBR } from '@material-ui/core/locale'
import { useLocation } from 'react-router-dom'

import typography from 'theme/typography'
import overrides from 'theme/overrides'
import palette from 'theme/palette'
import colors from 'theme/colors'

import { logowhite } from 'helpers/reports/assets/logoWhite64'

import CustomPaletteContext from 'contexts/CustomPaletteContext'

import * as service from 'service'

const CustomThemeProvider = ({ children, useAuthHook }) => {
  const [customTheme, setCustomTheme] = useState({})
  const [customPalette, setCustomPalette] = useState({})
  const [customLogo, setCustomLogo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [organization, setOrganization] = useState(null)
  const [customThemeIsActive, setCustomThemeIsActive] = useState(false)

  const location = useLocation()
  const { company } = useAuthHook()

  const isGuestScope = location.pathname.includes('/guests')

  const fetchData = async () => {
    setLoading(true)
    try {
      let layout = {}

      if (isGuestScope) {
        const guestTheme = company?.theme
        const guestLogo = company?.logo

        setCustomThemeIsActive(!!guestTheme)
        setCustomPalette(guestTheme || {})
        setOrganization(null)

        if (guestTheme) {
          layout = {
            main: guestTheme.primaryColor,
            secondary: guestTheme.secondaryColor,
            textPrimary: guestTheme.primaryFontColor,
            textSecondary: guestTheme.secondaryFontColor,
          }
        }

        setCustomLogo(guestLogo || null)

        const guestPaletteOverride = guestTheme
          ? {
              primary: {
                ...palette.primary,
                main: guestTheme.primaryColor,
                light: lighten(guestTheme.primaryColor, 0.9),
                dark: darken(guestTheme.primaryColor, 0.3),
                hover: darken(guestTheme.primaryColor, 0.2),
                contrastText: guestTheme.primaryFontColor,
              },
              secondary: {
                ...palette.secondary,
                main: guestTheme.secondaryColor,
                light: lighten(guestTheme.secondaryColor, 0.9),
                dark: darken(guestTheme.secondaryColor, 0.3),
                hover: darken(guestTheme.secondaryColor, 0.2),
                contrastText: guestTheme.secondaryFontColor,
              },
            }
          : {}

        const theme = createTheme(
          {
            colors: { ...colors },
            typography,
            overrides,
            zIndex: { appBar: 1200, drawer: 1100 },
            props: {
              MuiTextField: { variant: 'outlined' },
              MuiPaper: { variant: 'outlined' },
            },
            palette: {
              ...palette,
              ...guestPaletteOverride,
              ...(Object.keys(layout).length > 0 && { layout }),
            },
          },
          ptBR,
        )
        setCustomTheme(theme)
        return
      } else if (company?.id) {
        const isSupplierScope = location.pathname.includes('/suppliers')

        const themeService = isSupplierScope
          ? service.supplierPortal.whiteLabel
          : service.dponet.whiteLabel

        const response = await themeService.getStatusTheme({})
        const statusTheme = response?.data?.clientPortalThemeCompany?.status
        const themeData = response?.data?.clientPortalThemeCompany
        const logo =
          response?.data?.clientPortalThemeCompany?.clientPortalTheme?.logo
        setCustomThemeIsActive(themeData?.status ?? false)
        setCustomPalette(themeData)
        setOrganization(themeData?.clientPortalTheme?.organization)

        if (statusTheme) {
          layout = {
            main: themeData?.clientPortalTheme?.primaryColor,
            secondary: themeData?.clientPortalTheme?.secondaryColor,
            textPrimary: themeData?.clientPortalTheme?.primaryFontColor,
          }

          const getLogoUrl = async () => {
            const cachedLogoUrl = sessionStorage.getItem(logo)

            if (cachedLogoUrl) {
              return cachedLogoUrl
            } else {
              const logoUrl =
                response?.data?.clientPortalThemeCompany?.clientPortalTheme
                  ?.logo

              if (!logoUrl) return logoUrl

              const responseImage = await fetch(logoUrl)
                .then((res) => res)
                .catch((err) => null)

              if (responseImage) {
                const blob = await responseImage.blob()
                const localLogoUrl = URL.createObjectURL(blob)
                sessionStorage.setItem(logo, localLogoUrl)
                return localLogoUrl
              }
            }
          }

          const logoUrl = await getLogoUrl()
          setCustomLogo(logoUrl)
        } else {
          setCustomLogo(logowhite)
        }
      }

      const theme = createTheme(
        {
          colors: { ...colors },
          typography,
          overrides,
          zIndex: {
            appBar: 1200,
            drawer: 1100,
          },
          props: {
            MuiTextField: {
              variant: 'outlined',
            },
            MuiPaper: {
              variant: 'outlined',
            },
          },
          palette: {
            ...palette,
            ...(Object.keys(layout).length > 0 && { layout }),
          },
        },
        ptBR,
      )
      setCustomTheme(theme)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, isGuestScope])

  return (
    <CustomPaletteContext.Provider
      value={{
        customTheme,
        customPalette,
        customLogo,
        loading,
        setCustomLogo,
        isCompany: !!company?.id,
        organization,
        customThemeIsActive,
      }}
    >
      {children}
    </CustomPaletteContext.Provider>
  )
}

export default CustomThemeProvider
